import React, { useEffect, useState, useCallback } from 'react';
import { adminService, appService } from '../../services/api';
import { Plus, Search, Eye, Copy, RefreshCw, CheckCircle, XCircle } from 'lucide-react';
import ClientFormModal from './ClientFormModal';
import ClientDetailDrawer from './ClientDetailDrawer';
import './Clients.css';

const STATUS_DOT = { online: '#3ecf8e', degraded: '#f5a623', critical: '#ff4f6b', offline: '#ff4f6b', unknown: '#4a5268' };

export default function Clients() {
  const [clients, setClients]       = useState([]);
  const [search, setSearch]         = useState('');
  const [showForm, setShowForm]     = useState(false);
  const [editClient, setEditClient] = useState(null);
  const [drawer, setDrawer]         = useState(null);
  const [apps, setApps]             = useState([]);
  const [copiedKey, setCopiedKey]   = useState(null);

  const load = useCallback(async () => {
    const [cr, ar] = await Promise.all([adminService.listClients(), appService.getAll()]);
    setClients(cr.data.clients);
    setApps(ar.data.apps);
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = clients.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    c.code.toLowerCase().includes(search.toLowerCase())
  );

  const handleCopyKey = (key, id) => {
    navigator.clipboard.writeText(key);
    setCopiedKey(id);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  const getWorstStatus = (statusMap) => {
    if (statusMap?.offline || statusMap?.critical) return 'critical';
    if (statusMap?.degraded) return 'degraded';
    if (statusMap?.online) return 'online';
    return 'unknown';
  };

  const AMC_COLORS = { Platinum: '#7c8cf8', Gold: '#f5a623', Silver: '#8892aa', Bronze: '#cd7f32' };

  return (
    <div className="clients-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Clients</h1>
          <p className="page-subtitle">{clients.length} clients · manage onboarding, AMC tiers and widget keys</p>
        </div>
        <button className="btn btn-primary" onClick={() => { setEditClient(null); setShowForm(true); }}>
          <Plus size={14} /> Add Client
        </button>
      </div>

      <div className="clients-toolbar">
        <div className="search-box">
          <Search size={14} />
          <input placeholder="Search by name or code..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
      </div>

      <div className="clients-table card">
        <table>
          <thead>
            <tr>
              <th>Client</th>
              <th>AMC Tier</th>
              <th>Deployments</th>
              <th>Open Tickets</th>
              <th>AMC Status</th>
              <th>Widget Key</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(c => {
              const worst = getWorstStatus(c.deploymentStatus);
              const amcActive = c.amcEndDate && new Date(c.amcEndDate) > new Date();
              return (
                <tr key={c._id} className="client-row">
                  <td>
                    <div className="client-name">{c.name}</div>
                    <div className="client-code">{c.code} · {c.deploymentType}</div>
                  </td>
                  <td>
                    <span className="tier-badge" style={{ color: AMC_COLORS[c.amcTier?.name] || 'var(--text-muted)' }}>
                      {c.amcTier?.name || '—'}
                    </span>
                  </td>
                  <td>
                    <div className="dep-summary">
                      <span className="dep-dot" style={{ background: STATUS_DOT[worst] }} />
                      <span>{c.totalDeployments} deployment{c.totalDeployments !== 1 ? 's' : ''}</span>
                    </div>
                  </td>
                  <td>
                    <span className={`open-ticket-count ${c.openTickets > 0 ? 'has-tickets' : ''}`}>
                      {c.openTickets}
                    </span>
                  </td>
                  <td>
                    {amcActive
                      ? <span className="amc-ok"><CheckCircle size={12} /> Active</span>
                      : <span className="amc-exp"><XCircle size={12} /> {c.amcEndDate ? 'Expired' : 'Not set'}</span>
                    }
                  </td>
                  <td>
                    <div className="key-cell">
                      <code className="api-key">{c.widgetApiKey ? `${c.widgetApiKey.slice(0,16)}...` : '—'}</code>
                      {c.widgetApiKey && (
                        <button className="icon-btn-sm" onClick={() => handleCopyKey(c.widgetApiKey, c._id)} title="Copy key">
                          {copiedKey === c._id ? <CheckCircle size={12} color="var(--success)" /> : <Copy size={12} />}
                        </button>
                      )}
                    </div>
                  </td>
                  <td>
                    <button className="btn btn-ghost btn-sm" onClick={() => setDrawer(c)}>
                      <Eye size={13} /> View
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {filtered.length === 0 && <div className="empty-state">No clients found</div>}
      </div>

      {showForm && (
        <ClientFormModal
          client={editClient}
          apps={apps}
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); load(); }}
        />
      )}

      {drawer && (
        <ClientDetailDrawer
          clientId={drawer._id}
          onClose={() => setDrawer(null)}
          onEdit={(c) => { setDrawer(null); setEditClient(c); setShowForm(true); }}
          onRefresh={load}
        />
      )}
    </div>
  );
}
